import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ChevronLeft, ShieldCheck, Star } from 'lucide-react';
import { PricingPlans } from '../components/subscription/PricingPlans';
import confetti from 'canvas-confetti';
import { usePermissions } from '../hooks/queries/usePermissions';
import { useSubscriptionStatus } from '../hooks/queries/useSubscriptions';
import { useUser } from '../hooks/queries/useAuth';
import { useInitialLoadingScreenManager } from '../hooks/useInitialLoadingScreenManager';
import { useSmartRedirect } from '../hooks/useSmartRedirect';
import { logger } from '../utils/logger';

interface LocationState {
  reason?: string;
  message?: string;
  tier?: string;
}

export const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [currentBillingInterval, setCurrentBillingInterval] = useState<'month' | 'year'>('year');
  const { getSmartRedirectPath } = useSmartRedirect();

  // Get current user
  const { data: user } = useUser();

  // Use React Query for subscription status
  const { data: subscriptionStatus, isLoading: subscriptionLoading } = useSubscriptionStatus(
    user?.id
  );

  // Use React Query for permissions
  const { isVIP, loading: permissionsLoading } = usePermissions();

  // Loading screen management
  const { markRouteComplete } = useInitialLoadingScreenManager();

  const [messageInfo, setMessageInfo] = useState<{
    message: string;
    reason: string;
    tier?: string;
  } | null>(null);

  const state = location.state as LocationState;

  // Mark subscription page as fully loaded when data is ready
  // Using a ref to track if we've already marked complete to avoid infinite updates
  const hasMarkedCompleteRef = useRef(false);
  useEffect(() => {
    // Only mark as complete when both subscription and permissions loading are done
    if (!subscriptionLoading && !permissionsLoading && !hasMarkedCompleteRef.current) {
      hasMarkedCompleteRef.current = true;
      markRouteComplete();
    }
  }, [subscriptionLoading, permissionsLoading, markRouteComplete]);

  useEffect(() => {
    // Parse URL parameters for subscription info
    const reasonParam = searchParams.get('reason');
    const tierParam = searchParams.get('tier');

    if (reasonParam) {
      // If we have URL params, use those
      let message = '';

      if (reasonParam === 'project_sharing') {
        message =
          'Project sharing is only available with Team plans. Upgrade to enable collaboration.';
      } else if (reasonParam === 'project_limit') {
        message =
          "You've reached the maximum number of projects for your current plan. Upgrade to create more projects.";
      } else if (reasonParam === 'collaborator_limit' && tierParam) {
        if (tierParam === 'SMALL_TEAM') {
          message =
            "You've reached your Team Starter limit of 5 collaborators. Upgrade to Team Growth for more team members.";
        } else if (tierParam === 'MEDIUM_TEAM') {
          message =
            "You've reached your Team Growth limit of 20 collaborators. Upgrade to Team Gold for more team members.";
        } else {
          message =
            "You've reached your collaborator limit. Please upgrade your plan to add more team members.";
        }
      }

      if (message) {
        setMessageInfo({
          reason: reasonParam,
          message,
          tier: tierParam || undefined,
        });
      }
    }
    // If no URL params but we have state info, use that
    else if (state?.reason && state?.message) {
      setMessageInfo({
        reason: state.reason,
        message: state.message,
        tier: state.tier,
      });
    }
  }, [searchParams, state]);

  // Set billing interval based on subscription status
  useEffect(() => {
    if (subscriptionStatus?.isSubscribed) {
      // Check subscription metadata or plan info to determine interval
      const planId = subscriptionStatus.currentPlan?.planId;

      if (planId) {
        // If planId exists, determine interval from it
        const interval = planId.toLowerCase().includes('monthly') ? 'month' : 'year';
        setCurrentBillingInterval(interval);
      } else {
        // If planId doesn't exist, default to annual billing
        setCurrentBillingInterval('year');
        logger.component('No planId available, defaulting to yearly billing');
      }
    }
  }, [subscriptionStatus]);

  // Trigger confetti when VIP status is confirmed
  useEffect(() => {
    if (!subscriptionLoading && !permissionsLoading && isVIP) {
      confetti({
        particleCount: 150,
        spread: 300,
        origin: { y: 0.3 },
        colors: ['#3b82f6', '#60a5fa', '#93c5fd', '#2563eb', '#1d4ed8'],
        startVelocity: 45,
        gravity: 1.2,
        scalar: 1.2,
        ticks: 150,
      });
    }
  }, [isVIP, subscriptionLoading, permissionsLoading]);

  const handleGoBack = async () => {
    // Use smart redirect to determine the best path
    const redirectPath = await getSmartRedirectPath();
    logger.component('SubscriptionPage: Navigating back using smart redirect', {
      redirectPath,
    });
    navigate(redirectPath);
  };

  if (subscriptionLoading || permissionsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  // VIP view
  if (isVIP) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 to-purple-900 flex flex-col">
        {/* Header with Back Button */}
        <div className="relative flex items-center p-4 bg-black/20 backdrop-blur-sm">
          <button
            onClick={handleGoBack}
            className="absolute left-4 p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <ChevronLeft className="h-6 w-6 text-white" />
          </button>
          <h1 className="text-2xl font-bold flex-grow text-center text-white">VIP Status</h1>
        </div>

        {/* Main Content */}
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white/10 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 text-center">
            <div className="flex justify-center mb-6">
              <ShieldCheck className="text-green-400" size={64} strokeWidth={1.5} />
            </div>

            <h1 className="text-4xl font-bold text-white mb-4">VIP Access Granted</h1>

            <div className="text-xl text-gray-300 mb-6">
              You have exclusive access to all premium features with no limitations
            </div>

            <div className="bg-white/10 rounded-2xl p-6 mb-8 border border-white/20">
              <div className="flex items-center mb-4">
                <CheckCircle className="text-green-400 mr-3" size={24} />
                <span className="text-white">Unlimited Projects</span>
              </div>
              <div className="flex items-center mb-4">
                <CheckCircle className="text-green-400 mr-3" size={24} />
                <span className="text-white">Unlimited Team Collaborators</span>
              </div>
              <div className="flex items-center mb-4">
                <CheckCircle className="text-green-400 mr-3" size={24} />
                <span className="text-white">Full Feature Access</span>
              </div>
              <div className="flex items-center">
                <Star className="text-yellow-400 mr-3" size={24} />
                <span className="text-white">Priority Support</span>
              </div>
            </div>

            <p className="text-gray-400 italic mb-10">
              Enjoy your unlimited access to premium features
            </p>

            <button
              onClick={handleGoBack}
              className="bg-purple-500/80 hover:bg-purple-400/90 text-white transition-colors px-10 py-4 text-lg font-medium rounded-lg shadow-lg min-w-[200px] mb-6"
            >
              Return to Projects
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Direct display of pricing plans
  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Header with Back Button */}
      <div className="relative flex items-center p-4 bg-gray-900 border-b border-gray-800">
        <button
          onClick={handleGoBack}
          className="absolute left-4 p-2 hover:bg-gray-800 rounded-full transition-colors"
        >
          <ChevronLeft className="h-6 w-6 text-white" />
        </button>
        <h1 className="text-2xl font-bold flex-grow text-center text-white">Subscription Plans</h1>
      </div>

      {/* Show upgrade message if redirected from project limit */}
      {messageInfo && (
        <div className="mx-auto max-w-7xl w-full pt-10 px-4">
          <div className="mb-8 p-6 bg-amber-900/30 border-2 border-amber-500/50 rounded-xl text-center shadow-lg">
            <p className="text-xl font-medium text-amber-300 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6 mr-2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
              {messageInfo.message}
            </p>
          </div>
        </div>
      )}

      {/* Pricing Plans Component */}
      <div className="flex-grow flex flex-col items-center justify-center p-4 pb-[100px]">
        <div className="w-full max-w-7xl">
          <div className="mb-4 text-center">
            <h3 className="text-2xl font-bold text-white mb-2">
              Choose the plan that's right for you
            </h3>
            <p className="text-gray-400">From solo use to full team collaboration</p>
          </div>

          <div className="w-full">
            <PricingPlans initialBillingInterval={currentBillingInterval} isLandingPage={false} />
          </div>
        </div>
      </div>
    </div>
  );
};
